import "./PlaceChildOrderWizard.scss";
import template from "./PlaceChildOrderWizard.html";
import {IOnDestroy, IOnInit} from "angular";
import {EquityWizardData} from "../place-equity-order-wizard/models/EquityWizardData";
import {IModalInstanceService} from "angular-ui-bootstrap";
import {BehaviorSubject, Observable, ReplaySubject, Subject} from "rxjs";
import {EquityOrderWizardStore} from "../place-equity-order-wizard/store/EquityOrderWizardStore";
import {AccountSource} from "../../../../account/types/AccountSource";
import {AccountBriefRecord} from "../../../../account/types/AccountBriefRecord";
import {buildOrderRequest} from "../place-equity-order-wizard/util/EquityWizardOrderUtil";
import {DuplicateOrderSummaries} from "../types/DuplicateOrdersVettingResponse";
import {DuplicateOrdersNotificationDialog} from "../place-order-wizard/dialog/DuplicateOrdersNotificationDialog";
import {DuplicateOrderService} from "../services/DuplicateOrderService";
import {ReadonlyAccountStore} from "../../../../account/store/ReadonlyAccountStore";
import {AccountStore} from "../../../../account/store/AccountStore";
import {QuoteStore} from "../../../../quote/store/QuoteStore";
import {isNotNull} from "../../../../js/util/UtilFunctions";
import {AdviceTypes} from "../place-order-wizard/models/AdviceTypes";
import {OrderAdviceService} from "../services/OrderAdviceService";
import {PortfolioType} from "../../../models/Portfolio";
import {CurrentUserService} from "../../../../services/user/CurrentUserService";

interface Resolve {
    readonly order: EquityWizardData;
}

export const PlaceChildWizard = "tfPlaceChildOrderWizard";

export function placeChildWizard() {
    return {
        template,
        bindings: {
            modalInstance: "<",
            resolve: "<"
        },
        controller: PlaceChildOrderWizardController,
        controllerAs: "ctrl"
    };
}

export class PlaceChildOrderWizardController implements IOnInit, IOnDestroy {

    /** @input */
    public readonly resolve: Resolve;

    /** @input */
    public readonly modalInstance: IModalInstanceService;

    public readonly currentAccountBrief$ = new ReplaySubject<AccountBriefRecord | undefined>(1);
    public readonly adviceTypes$: Observable<AdviceTypes>;
    public readonly processingOrder$ = new BehaviorSubject(false);

    public readonly accountSource: AccountSource;
    public store: EquityOrderWizardStore;

    private readonly placeOrder$ = new Subject<boolean>();
    private readonly destroyed$ = new ReplaySubject<boolean>(1);
    private quoteStore: QuoteStore;

    /** @ngInject */
    public constructor(
                       private readonly currentUserService: CurrentUserService,
                       // private readonly portfolioService: PortfolioService,
                       private readonly orderAdviceService: OrderAdviceService,
                       // private readonly userConfig: UserConfig,
                       quoteStore: QuoteStore,
                       accountStore: AccountStore,
                       private readonly duplicateOrderService: DuplicateOrderService,
                       private readonly duplicateOrdersNotificationDialog: DuplicateOrdersNotificationDialog) {
        this.quoteStore = quoteStore;
        this.accountSource = ReadonlyAccountStore.create(accountStore);
        accountStore.refresh();
        this.adviceTypes$ = this.buildAdviceTypes();
    }

    private buildAdviceTypes(): Observable<AdviceTypes> {
        return this.accountSource
                   .current
                   .takeUntil(this.destroyed$)
                   .filter(isNotNull)
                   .filter((a) => a.portfolioType === PortfolioType.BROKER)
                   .map((a) => a.id)
                   .distinctUntilChanged()
                   .switchMap((id) => this.fetchAdviceTypes(id))
                   .publishReplay(1)
                   .refCount();
    }

    private fetchAdviceTypes(portfolioId: number): Observable<AdviceTypes> {
        return Observable.of(portfolioId)
                         .flatMap((id) => this.orderAdviceService.get(id))
                         .catch((err) => Observable.empty<AdviceTypes>());
    }

    public async $onInit() {
        this.store = new EquityOrderWizardStore(this.quoteStore, this.accountSource, this.resolve.order);
        this.accountSource.selectAccount(this.resolve.order.portfolio?.id ?? 0);
        this.watchAccountSelection();
        this.watchPlaceOrder();
    }

    private watchAccountSelection() {
        this.accountSource
            .current
            .takeUntil(this.destroyed$)
            .filter(isNotNull)
            .subscribe((portfolio) => this.store.updatePortfolio(portfolio));
    }

    private watchPlaceOrder() {
        this.placeOrder$
            .takeUntil(this.destroyed$)
            .withLatestFrom(this.processingOrder$)
            .filter(([_, processing]) => !processing)
            .do(() => this.processingOrder$.next(true))
            .withLatestFrom(this.store.current)
            .map(([_, order]) => order)
            .withLatestFrom(this.accountSource.current)
            .switchMap(([data, account]) => this.validateRequest(data, account))
            .subscribe((data) => this.modalInstance.close(data));
    }

    /* Empty if the request should not be submitted, otherwise return the request to submit */
    private validateRequest(data: EquityWizardData,
                            account: AccountBriefRecord | null): Observable<EquityWizardData> {
        if (account == null) {
            this.processingOrder$.next(false);
            return Observable.empty();
        }
        const request = buildOrderRequest(data);
        const result = this.duplicateOrderService.validate(account.id, [request]);
        return Observable.fromPromise(result)
                         .flatMap((res) => this.proceedIfDuplicates(res.duplicates))
                         .do((proceed) => console.debug("Order should proceed?", proceed))
                         .do(() => this.processingOrder$.next(false))
                         .filter((proceed) => proceed)
                         .do(() => console.debug("Order proceeded"))
                         .map(() => data);
    }

    private proceedIfDuplicates(duplicates: DuplicateOrderSummaries): Observable<boolean> {
        if (duplicates.length === 0) {
            return Observable.of(true);
        } else {
            return this.notifyDuplicates(duplicates);
        }
    }

    private notifyDuplicates(duplicates: DuplicateOrderSummaries): Observable<boolean> {
        return this.duplicateOrdersNotificationDialog.open(duplicates);
    }

    public placeOrder() {
        this.placeOrder$.next(true);
    }

    public cancel() {
        this.modalInstance.dismiss();
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
        this.store.destroy();
    }
}
